import React, { FC, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Pagination,
  Button,
  IconButton,
  Stack
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import { observer } from 'mobx-react-lite';

import Loader from 'components/loader';
import DraggableList from 'components/draggableList';
import Notification from 'components/notification';

import categoriesStore from 'store/categoriesStore';
import globalState from 'store/globalState';

import { isPaginated } from 'utilities/functions';

import { EnumPath, ICategory } from 'types/types';
import AddCategoryModal from 'components/modals/addCategory';

interface IParams {
  page: number;
  paginate: boolean;
  perpage: number;
}

const Categories: FC = () => {
  const { loading, categories } = categoriesStore;

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);
  const [notificationStatus, setNotificationStatus] = useState<
    'error' | 'success' | 'warning'
  >('error');
  const [notificationMsg, setNotificationMsg] = useState<string>('');

  const [params, setParams] = useState<IParams>({
    page: 1,
    perpage: 20,
    paginate: true
  });

  const notifyUser = useCallback(
    (message: string, status: 'error' | 'success') => {
      setNotificationMsg(message);
      setNotificationStatus(status);
      setIsNotificationOpen(true);
    },
    [setNotificationMsg, setNotificationStatus, setIsNotificationOpen]
  );

  const getCategories = () => {
    const response = categoriesStore.getCategories(params);

    response.then((val) => {
      if (!val.isOk) {
        notifyUser(val.msg, 'error');
      }
    });
  };

  const onCategoryAdd = (value: string) => {
    const response = categoriesStore.addCategory({
      name: value
    });

    response.then((val) => {
      notifyUser(val.msg, val.isOk ? 'success' : 'error');

      getCategories();
    });
  };

  useEffect(() => {
    const response = categoriesStore.getCategories(params);

    response.then((val) => {
      if (!val.isOk) {
        notifyUser(val.msg, 'error');
      }
    });

    globalState.setNewPathname('Категории');
    globalState.setRefreshFunction(categoriesStore.getCategories, params);
  }, []);

  useEffect(() => {
    const response = categoriesStore.getCategories(params);
    response.then((val) => {
      if (!val.isOk) {
        notifyUser(val.msg, 'error');
      }
    });

    globalState.setRefreshFunction(categoriesStore.getCategories, params);
  }, [params]);

  return (
    <Grid
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'scroll'
      }}
      item
      xs={12}
      md={12}
    >
      <Notification
        open={isNotificationOpen}
        message={notificationMsg}
        onClose={() => {
          setIsNotificationOpen(false);
          setNotificationMsg('');
        }}
        severity={notificationStatus}
      />
      <AddCategoryModal
        isOpen={modalIsOpen}
        setIsOpen={setModalIsOpen}
        onCategoryAdd={onCategoryAdd}
      />
      {loading ? (
        <Loader />
      ) : (
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'primary',
              width: 'auto',
              height: '48px'
            }}
          >
            <Typography variant="h6" sx={{ pl: 2 }}>
              Название
            </Typography>

            <Stack flexDirection="row">
              <IconButton onClick={() => setModalIsOpen(true)}>
                <AddCircleOutlineIcon color="primary" />
              </IconButton>
              <Button variant="text" onClick={() => setModalIsOpen(true)}>
                Создать
              </Button>
            </Stack>
          </Box>
          <DraggableList
            list={
              isPaginated<ICategory>(categories) ? categories.data : categories
            }
            pagesQuantity={
              isPaginated<ICategory>(categories) ? categories.last_page : 1
            }
            isSortable
            notifyUser={notifyUser}
            onReload={getCategories}
            path={EnumPath.categories}
          />
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              p: 1
            }}
          >
            <Pagination
              count={
                isPaginated<ICategory>(categories) ? categories.last_page : 1
              }
              onChange={(
                event: React.ChangeEvent<unknown>,
                pageNum: number
              ) => {
                setParams((value) => {
                  return {
                    ...value,
                    page: pageNum
                  };
                });
              }}
              page={params.page}
              shape="rounded"
            />
          </Box>
        </Box>
      )}
    </Grid>
  );
};

export default observer(Categories);
